// @wc-ignore-file
// (Error codes, column names and route names are data, not copy.)

/**
 * CR-183 — contributor suggestions.
 *
 * - A contributor lists their own suggestions through the definer RPC
 *   `list_my_cr_suggestions` (it never returns the CR id or number) and
 *   submits through the api-server `POST /cr-suggestions` route, which runs
 *   the overlap check and the service-role `submit_cr_suggestion`.
 * - A project member reads a CR's suggestions straight from `cr_suggestions`
 *   (RLS: members of the project), with the submitters' profiles and (CR-235)
 *   the files from `cr_suggestion_attachments` (same RLS) — never as CR-linked
 *   documents.
 */
import { invokeFunction } from "@/lib/functions";
import { supabase } from "@/lib/supabase";
import type {
  CrSuggestionClassification,
  MyCrSuggestion,
  MyCrSuggestionAttachment,
} from "@shared/types/supabase.types";

export type SubmitCrSuggestionInput = {
  projectId: string;
  classification: CrSuggestionClassification;
  title: string;
  description: string;
};

/** The route's error codes the UI explains. */
const ROUTE_ERROR_CODES = ["not_contributor", "rate_limited", "invalid_request"] as const;

/** The route's error codes, plus `unknown` for anything else (network, proxy page). */
export type CrSuggestionSubmitErrorCode = (typeof ROUTE_ERROR_CODES)[number] | "unknown";

function isRouteErrorCode(value: string): value is (typeof ROUTE_ERROR_CODES)[number] {
  return (ROUTE_ERROR_CODES as readonly string[]).includes(value);
}

export class CrSuggestionSubmitError extends Error {
  constructor(readonly code: CrSuggestionSubmitErrorCode) {
    super(`suggestion not submitted: ${code}`);
    this.name = "CrSuggestionSubmitError";
  }
}

/** The fields `POST /cr-suggestions` answers with (no CR id). */
export type SubmittedCrSuggestion = Pick<
  MyCrSuggestion,
  "id" | "classification" | "title" | "description" | "attached" | "public_status" | "created_at"
>;

/**
 * CR-235: one file on a suggestion, as the shared attachment row needs it —
 * the contributor RPC row and the member table row both fit.
 */
export type CrSuggestionAttachmentForMembers = Pick<
  MyCrSuggestionAttachment,
  "id" | "file_name" | "mime_type" | "size_bytes" | "storage_path"
>;

/** One suggestion on a CR, as a project member sees it on the CR page. */
export type CrSuggestionForMembers = {
  id: string;
  classification: CrSuggestionClassification;
  title: string;
  description: string;
  attached: boolean;
  createdAt: string;
  /** CR-235: the contributor's files, oldest first (the order they were added). */
  attachments: CrSuggestionAttachmentForMembers[];
  submitter: {
    userId: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
  };
};

export async function listMyCrSuggestions(projectId: string): Promise<MyCrSuggestion[]> {
  const { data, error } = await supabase.rpc("list_my_cr_suggestions", {
    p_project_id: projectId,
  });
  if (error) {
    throw new Error(`Failed to list your suggestions: ${error.message}`);
  }
  return data ?? [];
}

export async function submitCrSuggestion(
  input: SubmitCrSuggestionInput,
): Promise<SubmittedCrSuggestion> {
  const { data, error } = await invokeFunction<{ suggestion: SubmittedCrSuggestion }>(
    "cr-suggestions",
    { body: input },
  );
  if (error || !data?.suggestion) {
    const code = error?.message;
    throw new CrSuggestionSubmitError(code && isRouteErrorCode(code) ? code : "unknown");
  }
  return data.suggestion;
}

export async function listCrSuggestionsForCr(crId: string): Promise<CrSuggestionForMembers[]> {
  const { data, error } = await supabase
    .from("cr_suggestions")
    .select(
      "id, classification, title, description, attached, created_at, submitted_by, cr_suggestion_attachments(id, file_name, mime_type, size_bytes, storage_path, created_at)",
    )
    .eq("change_request_id", crId)
    .order("created_at", { ascending: true });
  if (error) {
    throw new Error(`Failed to list the suggestions of change request ${crId}: ${error.message}`);
  }
  const rows = data ?? [];
  if (rows.length === 0) {
    return [];
  }

  const userIds = [...new Set(rows.map((row) => row.submitted_by))];
  const { data: profiles, error: profilesError } = await supabase
    .from("profiles")
    .select("user_id, first_name, last_name, email")
    .in("user_id", userIds);
  if (profilesError) {
    throw new Error(`Failed to read the suggestion authors: ${profilesError.message}`);
  }
  const byUserId = new Map((profiles ?? []).map((profile) => [profile.user_id, profile]));

  return rows.map((row) => {
    const profile = byUserId.get(row.submitted_by);
    return {
      id: row.id,
      classification: row.classification as CrSuggestionClassification,
      title: row.title,
      description: row.description,
      attached: row.attached,
      createdAt: row.created_at,
      attachments: [...(row.cr_suggestion_attachments ?? [])]
        .sort((a, b) => a.created_at.localeCompare(b.created_at))
        .map(({ id, file_name, mime_type, size_bytes, storage_path }) => ({
          id,
          file_name,
          mime_type,
          size_bytes,
          storage_path,
        })),
      submitter: {
        userId: row.submitted_by,
        firstName: profile?.first_name ?? null,
        lastName: profile?.last_name ?? null,
        email: profile?.email ?? null,
      },
    };
  });
}
